import { useState, useRef, useCallback } from 'react';
import { fetchBaseUrl } from '../store/providers/Ajax';
import { useAuthenticationStore } from '../store/providers/Authentication';

// Hook to search activity templates by category with fixed activityTypeId=279
export function useActivityTemplateSearch() {
    const { fetchAuthData } = useAuthenticationStore();
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortRef = useRef();

    const search = useCallback(async (categoryId, query) => {
        if (!categoryId || !query || query.length < 3) {
            if (abortRef.current) { abortRef.current.abort(); }
            setOptions([]);
            setLoading(false);
            setError(null);
            return;
        }
        if (abortRef.current) { abortRef.current.abort(); }
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setError(null);
        try {
            const base = fetchBaseUrl();
            const auth = fetchAuthData();
            const emailActivityTypeId = 279;
            const url = `${base}/ActivityPlan/GetActivityTemplatesByCategory?categoryId=${categoryId}&activityTypeId=${emailActivityTypeId}&length=100&search=${encodeURIComponent(query)}`;
            const resp = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : {})
                },
                signal: controller.signal
            });
            if (!resp.ok) {
                throw new Error(`Status ${resp.status}`);
            }
            const data = await resp.json();
            // Map to include combined display label: "{planName} -> {name}" (fall back gracefully)
            const mapped = Array.isArray(data) ? data.map(item => ({
                ...item,
                // keep raw name/planName but add combined displayName property used by UI
                displayName: `${item.planName || ''}${item.planName && item.name ? ' -> ' : ''}${item.name || ''}`.trim()
            })) : [];
            setOptions(mapped);
        } catch (err) {
            if (err.name !== 'AbortError') {
                setError(err.message || 'Search failed');
            }
        } finally {
            setLoading(false);
        }
    }, [fetchAuthData]);

    return { options, loading, error, search };
}

export default useActivityTemplateSearch;
